import { useEffect, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import type { StoredDocument } from '@/modules/documents'
import {
  bookkeepAllPendingDocuments,
  listPendingBookkeepingDocuments,
  type BulkBookkeepingProgress,
  type BulkBookkeepingResult,
} from './bulkBookkeepingService'

interface BulkBookkeepingModalProps {
  officeUnitId: string
  client: AccountingClient | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

export function BulkBookkeepingModal({
  officeUnitId,
  client,
  isOpen,
  onClose,
  onSaved,
}: BulkBookkeepingModalProps) {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<StoredDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [incomingCount, setIncomingCount] = useState(0)
  const [outgoingCount, setOutgoingCount] = useState(0)
  const [progress, setProgress] = useState<BulkBookkeepingProgress | null>(null)
  const [result, setResult] = useState<BulkBookkeepingResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !client) return
    let cancelled = false
    setDocuments([])
    setIncomingCount(0)
    setOutgoingCount(0)
    setProgress(null)
    setResult(null)
    setError(null)
    setLoading(true)

    listPendingBookkeepingDocuments(client)
      .then((pending) => {
        if (cancelled) return
        setDocuments(pending.documents)
        setIncomingCount(pending.incoming)
        setOutgoingCount(pending.outgoing)
      })
      .catch((loadError) => {
        console.error('[BulkBookkeepingModal] Load error:', loadError)
        if (!cancelled) setError(t('accounting.bookkeeping.bulk.errors.load'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [client, isOpen, t])

  const handleStart = async () => {
    if (!client || documents.length === 0 || running) return
    setRunning(true)
    setError(null)
    setResult(null)

    try {
      const completed = await bookkeepAllPendingDocuments(
        officeUnitId,
        client,
        documents,
        setProgress,
      )
      setResult(completed)
      if (completed.booked > 0) onSaved()
    } catch (runError) {
      console.error('[BulkBookkeepingModal] Orchestration error:', runError)
      setError(t('accounting.bookkeeping.bulk.errors.run'))
    } finally {
      setRunning(false)
      setProgress(null)
    }
  }

  const close = () => {
    if (!running) onClose()
  }

  const failureMessage = (code: string) => {
    const translated = t(`accounting.bookkeeping.errors.${code}`)
    return translated.startsWith('accounting.') ? code : translated
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('accounting.bookkeeping.bulk.title')}
      size="sm"
      onSubmit={result ? undefined : handleStart}
      submitLabel={t('accounting.bookkeeping.bulk.confirm')}
      submitting={loading || running || documents.length === 0 || Boolean(error)}
      closeLabel={t('actions.close')}
      showDefaultCloseButton={!running}
    >
      {loading ? (
        <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('accounting.bookkeeping.bulk.loading')}
        </p>
      ) : error ? (
        <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
          {error}
        </p>
      ) : running && progress ? (
        <div className="space-y-4 py-4">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            {t('accounting.bookkeeping.bulk.progress', {
              current: String(progress.current),
              total: String(progress.total),
            })}
          </p>
          <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <span
              className="block h-full bg-emerald-500 transition-all"
              style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
            />
          </div>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400" title={progress.documentName}>
            {progress.documentName}
          </p>
        </div>
      ) : result ? (
        <div className="space-y-4">
          <div className={`rounded-xl p-4 ${result.failed === 0
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
            <p className="font-semibold">{t('accounting.bookkeeping.bulk.completed')}</p>
            <p className="mt-1 text-sm">{t('accounting.bookkeeping.bulk.result', {
              booked: String(result.booked),
              failed: String(result.failed),
            })}</p>
          </div>
          {result.failures.length > 0 && (
            <ul className="max-h-52 space-y-2 overflow-y-auto text-xs text-slate-600 dark:text-slate-300">
              {result.failures.map((failure) => (
                <li key={failure.documentId} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                  <p className="truncate font-medium" title={failure.documentName}>{failure.documentName}</p>
                  <p className="mt-1 text-slate-500 dark:text-slate-400">
                    {failureMessage(failure.code)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : documents.length === 0 ? (
        <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
          {t('accounting.bookkeeping.bulk.empty')}
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('accounting.bookkeeping.bulk.description', {
              client: client?.name || '',
              count: String(documents.length),
            })}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
              <p className="text-xs text-slate-500">{t('accounting.clients.documentMovements.incoming')}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{incomingCount}</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
              <p className="text-xs text-slate-500">{t('accounting.clients.documentMovements.outgoing')}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{outgoingCount}</p>
            </div>
          </div>
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200">
            {t('accounting.bookkeeping.bulk.memoryRule')}
          </div>
        </div>
      )}
    </Modal>
  )
}
